/**
 * Unified Authoritative Scoring, XP, Level, Accuracy, Tier & Number Display Configuration
 *
 * Principles:
 * - Small numbers: a session score is in the hundreds, XP in the tens.
 * - Each number means one thing: Score = this session. XP = lifetime progress. Accuracy = skill.
 * - All constants live in this single config object.
 * - Score and XP are computed server-side, never trusted from the client.
 */

export const SCORING_CONFIG = {
  // 1. Score (per session, per question)
  SCORE: {
    CORRECT_BASE: 100,
    SPEED_BONUS_MIN: 0,
    SPEED_BONUS_MAX: 50,
    STREAK_BONUS_PER_STEP: 10,
    STREAK_BONUS_MAX: 50,
    WRONG_SKIP_TIMEOUT_POINTS: 0,
  },

  // 2. XP (lifetime progress)
  XP: {
    SCORE_DIVISOR: 10,
    MIN_PER_CORRECT_ANSWER: 1,
    PERFECT_SESSION_BONUS: 20,
  },

  // 3. Level: Level = floor(sqrt(XP / 100)) + 1
  LEVEL: {
    XP_DIVISOR: 100,
    BASE_LEVEL: 1,
    TITLES: [
      { minLevel: 10, title: 'Grand Theologian' },
      { minLevel: 8, title: 'Apostolic Herald' },
      { minLevel: 6, title: 'Prophetic Voice' },
      { minLevel: 4, title: 'Temple Scribe' },
      { minLevel: 3, title: 'Covenant Scholar' },
      { minLevel: 2, title: 'Berean Student' },
      { minLevel: 1, title: 'Scripture Seeker' },
    ] as const,
  },

  // 4. Accuracy & Unlock Thresholds
  ACCURACY: {
    LIFETIME_UNLOCK_MIN_QUESTIONS: 20,
    LIFETIME_LOCKED_MESSAGE: 'Play 20 questions to unlock',
    TOP_ACCURACY_BOARD_MIN_QUESTIONS: 30,
  },

  // 5. Evaluation (result card tiers)
  EVALUATION: {
    MIN_QUESTIONS_FOR_RANK_AND_TIER: 3,
    TIERS: [
      {
        minAccuracy: 100,
        key: 'perfect',
        label: 'Perfect',
        trophy: 'gold' as const,
      },
      {
        minAccuracy: 80,
        key: 'excellent',
        label: 'Excellent',
        trophy: 'gold' as const,
      },
      {
        minAccuracy: 60,
        key: 'good',
        label: 'Good',
        trophy: 'silver' as const,
      },
      {
        minAccuracy: 40,
        key: 'keep_going',
        label: 'Keep Going',
        trophy: 'bronze' as const,
      },
      {
        minAccuracy: 0,
        key: 'try_again',
        label: 'Try Again',
        trophy: 'none' as const,
      },
    ] as const,
  },

  // 8. Number display
  DISPLAY: {
    COMPACT_K_THRESHOLD: 10000,
    ZERO_PLACEHOLDER: '–',
  },
} as const;

export type EvaluationTrophyType = 'gold' | 'silver' | 'bronze' | 'none';

export type EvaluationTierKey =
  | 'perfect'
  | 'excellent'
  | 'good'
  | 'keep_going'
  | 'try_again'
  | 'unranked';

export interface EvaluationTierInfo {
  key: EvaluationTierKey;
  label: string;
  tier: string;
  trophy: EvaluationTrophyType;
  isRanked: boolean;
  isTiered: boolean;
}

export interface QuestionScoreInput {
  isCorrect: boolean;
  timeTakenMs: number;
  timerSeconds: number;
  timerEnabled: boolean;
  currentStreak: number;
}

export interface QuestionScoreBreakdown {
  isCorrect: boolean;
  basePoints: number;
  speedBonus: number;
  streakBonus: number;
  totalPoints: number;
  newStreak: number;
}

export interface SessionScoreSummary {
  score: number;
  xpGained: number;
  perfectBonusXp: number;
  correctCount: number;
  wrongCount: number;
  skippedCount: number;
  timeoutCount: number;
  totalQuestions: number;
  accuracyPercent: number;
  accuracyFractionText: string;
  skipWrongBreakdownText: string | null;
  bestStreak: number;
  evaluation: EvaluationTierInfo;
}

export interface LevelProgressInfo {
  level: number;
  xp: number;
  currentLevelStartXp: number;
  nextLevelStartXp: number;
  currentLevelXp: number;
  nextLevelXp: number;
  progressPercent: number;
  rankTitle: string;
  summaryText: string;
}

/**
 * 1. Per-question score calculation
 * - Correct: 100
 * - Speed bonus: 0–50, linear with time left
 * - Streak bonus: +10 × current streak, capped at +50
 * - Wrong, skipped or timeout: 0, no penalty
 * - No mode, format, or difficulty multipliers.
 */
export function calculateQuestionScore(
  input: QuestionScoreInput
): QuestionScoreBreakdown {
  if (!input.isCorrect) {
    return {
      isCorrect: false,
      basePoints: SCORING_CONFIG.SCORE.WRONG_SKIP_TIMEOUT_POINTS,
      speedBonus: 0,
      streakBonus: 0,
      totalPoints: SCORING_CONFIG.SCORE.WRONG_SKIP_TIMEOUT_POINTS,
      newStreak: 0,
    };
  }

  const basePoints = SCORING_CONFIG.SCORE.CORRECT_BASE;
  const newStreak = Math.max(0, Math.floor(input.currentStreak || 0)) + 1;

  let speedBonus = 0;
  if (input.timerEnabled && input.timerSeconds > 0) {
    const maxTimeMs = input.timerSeconds * 1000;
    const clampedTakenMs = Math.max(0, Math.min(maxTimeMs, input.timeTakenMs || 0));
    const timeLeftMs = maxTimeMs - clampedTakenMs;
    const ratioLeft = timeLeftMs / maxTimeMs;
    speedBonus = Math.round(
      SCORING_CONFIG.SCORE.SPEED_BONUS_MAX * Math.max(0, Math.min(1, ratioLeft))
    );
  }

  const streakBonus = Math.min(
    SCORING_CONFIG.SCORE.STREAK_BONUS_MAX,
    newStreak * SCORING_CONFIG.SCORE.STREAK_BONUS_PER_STEP
  );

  const totalPoints = basePoints + speedBonus + streakBonus;

  return {
    isCorrect: true,
    basePoints,
    speedBonus,
    streakBonus,
    totalPoints,
    newStreak,
  };
}

/**
 * 2. Session XP calculation
 * - XP = round(score / 10), minimum 1 per correct answer
 * - Perfect session (all correct): +20 XP
 */
export function calculateSessionXp(
  score: number,
  correctCount: number,
  totalQuestions: number
): number;
export function calculateSessionXp(params: {
  score: number;
  correctCount: number;
  totalQuestions: number;
}): { xpGained: number; baseXp: number; perfectBonusXp: number };
export function calculateSessionXp(
  arg1: number | { score: number; correctCount: number; totalQuestions: number },
  arg2?: number,
  arg3?: number
): any {
  const isObj = typeof arg1 === 'object' && arg1 !== null;
  const safeScore = Math.max(0, Math.round((isObj ? arg1.score : arg1) || 0));
  const safeCorrect = Math.max(0, Math.floor((isObj ? arg1.correctCount : arg2) || 0));
  const safeTotal = Math.max(0, Math.floor((isObj ? arg1.totalQuestions : arg3) || 0));

  const rawFromScore = Math.round(safeScore / SCORING_CONFIG.XP.SCORE_DIVISOR);
  const minFromCorrect = safeCorrect * SCORING_CONFIG.XP.MIN_PER_CORRECT_ANSWER;
  const baseXp = safeCorrect > 0 ? Math.max(minFromCorrect, rawFromScore) : rawFromScore;

  const isPerfectSession = safeTotal > 0 && safeCorrect === safeTotal;
  const perfectBonusXp = isPerfectSession
    ? SCORING_CONFIG.XP.PERFECT_SESSION_BONUS
    : 0;
  const xpGained = baseXp + perfectBonusXp;

  if (!isObj) {
    return xpGained;
  }

  return {
    xpGained,
    baseXp,
    perfectBonusXp,
  };
}

/**
 * 3. Level calculation
 * - Level = floor(sqrt(XP / 100)) + 1
 * - Thresholds: L2 = 100, L3 = 400, L4 = 900, L5 = 1,600, L10 = 8,100
 * - Display: "Level 4 · 120 / 200 to next"
 */
export function getLevelThresholdXp(level: number): number {
  const safeLevel = Math.max(1, Math.floor(level));
  return SCORING_CONFIG.LEVEL.XP_DIVISOR * (safeLevel - 1) * (safeLevel - 1);
}

export function getRankTitleForLevel(level: number): string {
  for (const band of SCORING_CONFIG.LEVEL.TITLES) {
    if (level >= band.minLevel) {
      return band.title;
    }
  }
  return 'Scripture Seeker';
}

export function calculateLevelFromXp(xp: number): LevelProgressInfo {
  const safeXp = Math.max(0, Math.floor(Number(xp) || 0));
  const level =
    Math.floor(Math.sqrt(safeXp / SCORING_CONFIG.LEVEL.XP_DIVISOR)) +
    SCORING_CONFIG.LEVEL.BASE_LEVEL;

  const currentLevelStartXp = getLevelThresholdXp(level);
  const nextLevelStartXp = getLevelThresholdXp(level + 1);
  const currentLevelXp = Math.max(0, safeXp - currentLevelStartXp);
  const nextLevelXp = Math.max(1, nextLevelStartXp - currentLevelStartXp);
  const progressPercent = Math.min(
    100,
    Math.max(0, Math.round((currentLevelXp / nextLevelXp) * 100))
  );
  const rankTitle = getRankTitleForLevel(level);
  const summaryText = `Level ${level} · ${ currentLevelXp.toLocaleString('en-US') } / ${ nextLevelXp.toLocaleString('en-US') } to next`;

  return {
    level,
    xp: safeXp,
    currentLevelStartXp,
    nextLevelStartXp,
    currentLevelXp,
    nextLevelXp,
    progressPercent,
    rankTitle,
    summaryText,
  };
}

/**
 * 4. Accuracy calculation & formatting
 * - Accuracy = correct / total questions in the session
 * - Skips and timeouts count as wrong, so X/Y and percent always match.
 * - If skips or timeouts occurred, build small breakdown line e.g. "1 skipped, 1 wrong"
 */
export function calculateSessionAccuracy(params: {
  correctCount: number;
  totalQuestions: number;
  skippedCount?: number;
  timeoutCount?: number;
}): {
  correctCount: number;
  totalQuestions: number;
  wrongCount: number;
  skippedCount: number;
  timeoutCount: number;
  accuracyPercent: number;
  percent: number;
  accuracyFractionText: string;
  primaryLabel: string;
  secondaryLabel: string;
  skipWrongBreakdownText: string | null;
  breakdownText: string | null;
} {
  const totalQuestions = Math.max(0, Math.floor(params.totalQuestions || 0));
  const correctCount = Math.max(
    0,
    Math.min(totalQuestions, Math.floor(params.correctCount || 0))
  );
  const skippedCount = Math.max(0, Math.floor(params.skippedCount || 0));
  const timeoutCount = Math.max(0, Math.floor(params.timeoutCount || 0));
  const totalIncorrect = Math.max(0, totalQuestions - correctCount);
  const wrongCount = Math.max(0, totalIncorrect - skippedCount - timeoutCount);

  const accuracyPercent =
    totalQuestions > 0 ? Math.round((correctCount / totalQuestions) * 100) : 0;
  const accuracyFractionText = `${correctCount}/${totalQuestions} correct`;
  const secondaryLabel = `${accuracyPercent}%`;

  let skipWrongBreakdownText: string | null = null;
  const combinedSkipped = skippedCount + timeoutCount;
  if (combinedSkipped > 0) {
    skipWrongBreakdownText = `${combinedSkipped} skipped, ${wrongCount} wrong`;
  }

  return {
    correctCount,
    totalQuestions,
    wrongCount,
    skippedCount,
    timeoutCount,
    accuracyPercent,
    percent: accuracyPercent,
    accuracyFractionText,
    primaryLabel: accuracyFractionText,
    secondaryLabel,
    skipWrongBreakdownText,
    breakdownText: skipWrongBreakdownText,
  };
}

export function formatLifetimeAccuracy(
  arg1: number | { totalCorrect: number; totalQuestionsSeen: number },
  arg2?: number
): {
  unlocked: boolean;
  accuracyPercent: number | null;
  percent: number | null;
  displayText: string;
  label: string;
  questionsRemaining: number;
  questionsSeen: number;
  correctCount: number;
} {
  const isObj = typeof arg1 === 'object' && arg1 !== null;
  const totalSeen = Math.max(
    0,
    Math.floor((isObj ? arg1.totalQuestionsSeen : arg2) || 0)
  );
  const totalCorrect = Math.max(
    0,
    Math.min(totalSeen, Math.floor((isObj ? arg1.totalCorrect : arg1) || 0))
  );

  if (totalSeen < SCORING_CONFIG.ACCURACY.LIFETIME_UNLOCK_MIN_QUESTIONS) {
    const lockedMsg = SCORING_CONFIG.ACCURACY.LIFETIME_LOCKED_MESSAGE;
    return {
      unlocked: false,
      accuracyPercent: null,
      percent: null,
      displayText: lockedMsg,
      label: lockedMsg,
      questionsRemaining:
        SCORING_CONFIG.ACCURACY.LIFETIME_UNLOCK_MIN_QUESTIONS - totalSeen,
      questionsSeen: totalSeen,
      correctCount: totalCorrect,
    };
  }

  const accuracyPercent = Math.round((totalCorrect / totalSeen) * 100);
  const displayText = `${accuracyPercent}% (${totalCorrect}/${totalSeen} correct)`;
  return {
    unlocked: true,
    accuracyPercent,
    percent: accuracyPercent,
    displayText,
    label: displayText,
    questionsRemaining: 0,
    questionsSeen: totalSeen,
    correctCount: totalCorrect,
  };
}

/**
 * 5. Evaluation (result card tiers)
 * - 100%: Perfect (gold trophy)
 * - 80-99%: Excellent (gold)
 * - 60-79%: Good (silver)
 * - 40-59%: Keep Going (bronze)
 * - Under 40%: Try Again (no trophy)
 * - Sessions under 3 questions earn XP but are not ranked or tiered.
 */
export function getEvaluationTier(
  arg1: number | { accuracyPercent: number; totalQuestions: number },
  arg2?: number
): EvaluationTierInfo {
  const isObj = typeof arg1 === 'object' && arg1 !== null;
  const totalQuestions = Math.max(
    0,
    Math.floor((isObj ? arg1.totalQuestions : arg2 ?? 10) || 0)
  );
  const accuracy = Math.max(
    0,
    Math.min(100, Math.round((isObj ? arg1.accuracyPercent : arg1) || 0))
  );

  if (totalQuestions < SCORING_CONFIG.EVALUATION.MIN_QUESTIONS_FOR_RANK_AND_TIER) {
    return {
      key: 'unranked',
      label: 'Unranked Session (< 3 questions)',
      tier: 'Unranked Session (< 3 questions)',
      trophy: 'none',
      isRanked: false,
      isTiered: false,
    };
  }

  for (const tier of SCORING_CONFIG.EVALUATION.TIERS) {
    if (accuracy >= tier.minAccuracy) {
      return {
        key: tier.key,
        label: tier.label,
        tier: tier.label,
        trophy: tier.trophy,
        isRanked: true,
        isTiered: true,
      };
    }
  }

  return {
    key: 'try_again',
    label: 'Try Again',
    tier: 'Try Again',
    trophy: 'none',
    isRanked: true,
    isTiered: true,
  };
}

/**
 * 8. Number display
 * - Full number under 10,000 with commas, then K (12.4K).
 * - Zero shows "–" in dim gray.
 */
export function formatDisplayNumber(
  value: number | null | undefined,
  options?: { allowZeroNumber?: boolean }
): { text: string; isZero: boolean } {
  const num = Math.round(Number(value) || 0);
  if (num <= 0 && !options?.allowZeroNumber) {
    return {
      text: SCORING_CONFIG.DISPLAY.ZERO_PLACEHOLDER,
      isZero: true,
    };
  }
  if (num >= SCORING_CONFIG.DISPLAY.COMPACT_K_THRESHOLD) {
    const kVal = num / 1000;
    const formattedK =
      kVal >= 100
        ? `${Math.round(kVal)}K`
        : `${Number(kVal.toFixed(1))}K`;
    return {
      text: formattedK,
      isZero: false,
    };
  }
  return {
    text: num.toLocaleString('en-US'),
    isZero: false,
  };
}

/**
 * Sort comparator for Leaderboard entries:
 * - Period tabs (Week, Month, All-Time, Country) rank by XP earned in that period.
 * - Tie-breakers: higher accuracy, then earlier timestamp.
 */
export function compareLeaderboardEntries(
  a: {
    periodXp?: number;
    xp?: number;
    accuracy?: number;
    updatedAt?: string | Date | number | null;
  },
  b: {
    periodXp?: number;
    xp?: number;
    accuracy?: number;
    updatedAt?: string | Date | number | null;
  }
): number {
  const xpA = Number(a.periodXp ?? a.xp) || 0;
  const xpB = Number(b.periodXp ?? b.xp) || 0;
  if (xpB !== xpA) {
    return xpB - xpA;
  }
  const accA = Number(a.accuracy) || 0;
  const accB = Number(b.accuracy) || 0;
  if (accB !== accA) {
    return accB - accA;
  }
  const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : Number.MAX_SAFE_INTEGER;
  const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : Number.MAX_SAFE_INTEGER;
  return timeA - timeB;
}

/**
 * Sort comparator for "Top Accuracy" board:
 * - Minimum 30 questions, ranked by accuracy, then XP, then earlier timestamp.
 */
export function compareTopAccuracyEntries(
  a: {
    accuracy: number;
    totalXp?: number;
    xp?: number;
    updatedAt?: string | Date | number | null;
  },
  b: {
    accuracy: number;
    totalXp?: number;
    xp?: number;
    updatedAt?: string | Date | number | null;
  }
): number {
  if (b.accuracy !== a.accuracy) {
    return b.accuracy - a.accuracy;
  }
  const xpA = Number(a.totalXp ?? a.xp) || 0;
  const xpB = Number(b.totalXp ?? b.xp) || 0;
  if (xpB !== xpA) {
    return xpB - xpA;
  }
  const timeA = a.updatedAt ? new Date(a.updatedAt).getTime() : Number.MAX_SAFE_INTEGER;
  const timeB = b.updatedAt ? new Date(b.updatedAt).getTime() : Number.MAX_SAFE_INTEGER;
  return timeA - timeB;
}

/**
 * 9. Tooltip Copy Builders
 * Dynamically generated from SCORING_CONFIG to ensure text never drifts from real rules.
 */
export type TooltipTranslationDict = {
  tooltipScore?: string;
  tooltipAccuracy?: string;
  tooltipBestStreak?: string;
  tooltipXpGained?: string;
  tooltipLevelProgress?: string;
  tooltipLifetimeAccuracy?: string;
  [key: string]: any;
};

export function getScoreTooltipText(t?: TooltipTranslationDict): string {
  if (t?.tooltipScore) {
    return t.tooltipScore
      .replace('{correctBase}', String(SCORING_CONFIG.SCORE.CORRECT_BASE))
      .replace('{speedBonusMax}', String(SCORING_CONFIG.SCORE.SPEED_BONUS_MAX))
      .replace('{streakStep}', String(SCORING_CONFIG.SCORE.STREAK_BONUS_PER_STEP))
      .replace('{streakMax}', String(SCORING_CONFIG.SCORE.STREAK_BONUS_MAX))
      .replace('{wrongPoints}', String(SCORING_CONFIG.SCORE.WRONG_SKIP_TIMEOUT_POINTS));
  }
  return `Your points for this game only. Each correct answer earns ${SCORING_CONFIG.SCORE.CORRECT_BASE} points. Answer faster for up to ${SCORING_CONFIG.SCORE.SPEED_BONUS_MAX} bonus points. Getting answers right in a row adds ${SCORING_CONFIG.SCORE.STREAK_BONUS_PER_STEP} bonus points per answer in the streak, up to ${SCORING_CONFIG.SCORE.STREAK_BONUS_MAX}. Wrong, skipped or timed-out answers earn ${SCORING_CONFIG.SCORE.WRONG_SKIP_TIMEOUT_POINTS}, and nothing is ever taken away.`;
}

export function getAccuracyTooltipText(t?: TooltipTranslationDict): string {
  if (t?.tooltipAccuracy) return t.tooltipAccuracy;
  return 'How many questions you got right out of all the questions in this game. Skipped and timed-out questions count as wrong. Example: 2 of 5 correct = 40%.';
}

export function getBestStreakTooltipText(t?: TooltipTranslationDict): string {
  if (t?.tooltipBestStreak) return t.tooltipBestStreak;
  return 'Your longest run of correct answers in a row during this game. A wrong, skipped or timed-out answer resets the streak.';
}

export function getXpGainedTooltipText(t?: TooltipTranslationDict): string {
  if (t?.tooltipXpGained) {
    return t.tooltipXpGained
      .replace('{scoreDivisor}', String(SCORING_CONFIG.XP.SCORE_DIVISOR))
      .replace('{minPerCorrect}', String(SCORING_CONFIG.XP.MIN_PER_CORRECT_ANSWER))
      .replace('{perfectBonus}', String(SCORING_CONFIG.XP.PERFECT_SESSION_BONUS));
  }
  return `XP is your long-term progress. You earn about 1 XP for every ${SCORING_CONFIG.XP.SCORE_DIVISOR} points of Score, at least ${SCORING_CONFIG.XP.MIN_PER_CORRECT_ANSWER} XP per correct answer, plus ${SCORING_CONFIG.XP.PERFECT_SESSION_BONUS} bonus XP for a perfect game. XP raises your level and your rank on the leaderboard.`;
}

export function getLevelProgressTooltipText(t?: TooltipTranslationDict): string {
  if (t?.tooltipLevelProgress) return t.tooltipLevelProgress;
  return 'Level goes up as you earn XP. Each new level needs more XP than the last.';
}

export function getLifetimeAccuracyTooltipText(t?: TooltipTranslationDict): string {
  if (t?.tooltipLifetimeAccuracy) {
    return t.tooltipLifetimeAccuracy.replace(
      '{minQuestions}',
      String(SCORING_CONFIG.ACCURACY.LIFETIME_UNLOCK_MIN_QUESTIONS)
    );
  }
  return `Shown after you've played ${SCORING_CONFIG.ACCURACY.LIFETIME_UNLOCK_MIN_QUESTIONS} questions.`;
}


